import useAutocomplete from "@mui/material/useAutocomplete";
import { useEffect, useState, type ReactNode } from "react";
import { LoadingNote } from "@/components/LoadingNote";
import { ComboboxField } from "@/components/SearchSelect";
import { FIELD_OPTION } from "@/components/fieldStyles";
import { useDebouncedValue } from "@/lib/useDebouncedValue";

// BRK-S138 — the debounced server-backed typeahead; BRK-S314 made it a
// searchable select.
//
// `ClassCodeField` (BRK-S047/S065) and the NAICS picker on the Surety account
// profile both want the same shell: type at least two characters, wait ~300 ms,
// search the server, list what came back. The part that would drift if each
// kept a copy is the CANCELLATION, the half nobody looks at until a stale
// response overwrites a fresh one, so it lives here once. The pause itself is
// `useDebouncedValue`'s, which the list views' search shares (BRK-S315).
//
// Since BRK-S314 it is `SearchSelect`'s sibling on MUI's `useAutocomplete` and
// the same `ComboboxField` parts: a combobox named by its visible label, arrow
// keys and Enter, the pick shown in the box, a clear button unless the field is
// required. A broker can't tell a server search from a static list by how it
// behaves. The pick stays while the broker types over it to look for another,
// and comes back on blur if they don't choose one, as a select's would.
//
// `search` REPORTS ITS OWN EMPTY MESSAGE. The class-code search is SCOPED (to a
// submission's product and the insured's state, PMS-S156), so an empty page
// there has a reason ("no eligible codes for TX on this product", "add the
// insured's state"), and a bare "no results" would be a lie about appetite.
// Letting the search return the sentence keeps that knowledge in the caller
// that owns the endpoint. A search that FAILED is no miss: it says it didn't
// go through, with Try again (BRK-S315, NN #7).
//
// ⚠️ `search` MUST BE REFERENTIALLY STABLE. It is an effect dependency, so a
// fresh closure per render restarts the debounce on every render — which is an
// unbounded request loop, not a slow one. Wrap it in `useCallback`.

/** What one search resolved to: the rows, and — when there are none — why. */
export interface TypeaheadResult<T> {
  items: T[];
  /**
   * What to say when `items` is empty. Optional: absent falls back to the
   * generic line, which is the honest answer for a plain search miss.
   */
  emptyMessage?: ReactNode;
}

export function Typeahead<T>({
  label,
  placeholder,
  search,
  selected,
  onSelect,
  keyOf,
  labelOf,
  renderOption,
  error,
  detail,
  required,
  optional,
  disabled,
  id,
  minChars = 2,
  debounceMs = 300,
  searchingLabel = "Searching…",
}: {
  /** The visible label, and the combobox's accessible name. */
  label: string;
  placeholder?: string;
  /** MUST be stable (see the header). Resolves the rows and any empty line. */
  search: (query: string) => Promise<TypeaheadResult<T>>;
  selected: T | null;
  /** `null` clears the pick. */
  onSelect: (item: T | null) => void;
  keyOf: (item: T) => string;
  /** How a pick reads in the box. */
  labelOf: (item: T) => string;
  renderOption: (item: T) => ReactNode;
  error?: string;
  /** A line under the box about the pick (`ComboboxField`'s). */
  detail?: string;
  required?: boolean;
  optional?: boolean;
  disabled?: boolean;
  id?: string;
  minChars?: number;
  debounceMs?: number;
  /** What it says beside its spinner while the search runs: what it's
   *  searching, in a few words ("Searching class codes…"; BRK-S488). */
  searchingLabel?: string;
}) {
  // What the broker typed, as opposed to the box's text: a pick puts its label
  // in the box, and that is not a search.
  const [query, setQuery] = useState("");
  const typed = query.trim();
  const settled = useDebouncedValue(typed, debounceMs);
  const [result, setResult] = useState<TypeaheadResult<T> | null>(null);
  const [failed, setFailed] = useState(false);
  const [searching, setSearching] = useState(false);
  // Try again asks the same query once more.
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    if (settled.length < minChars) {
      setResult(null);
      setFailed(false);
      setSearching(false);
      return;
    }
    let cancelled = false;
    setSearching(true);
    search(settled)
      .then(
        (next) => {
          // A LATE response for a query the broker has moved past must not
          // overwrite the current one — the whole reason the flag is checked
          // after the await and not only before it.
          if (cancelled) return;
          setResult(next);
          setFailed(false);
        },
        () => {
          if (cancelled) return;
          setResult(null);
          setFailed(true);
        },
      )
      .finally(() => {
        if (!cancelled) setSearching(false);
      });
    return () => {
      cancelled = true;
    };
  }, [settled, search, minChars, attempt]);

  const items = result?.items ?? [];
  const combo = useAutocomplete({
    id,
    options: items,
    value: selected,
    onChange: (_event, item) => onSelect(item),
    onInputChange: (_event, text, reason) =>
      setQuery(reason === "input" ? text : ""),
    getOptionLabel: labelOf,
    getOptionKey: keyOf,
    isOptionEqualToValue: (a, b) => keyOf(a) === keyOf(b),
    // The server has filtered already.
    filterOptions: (rows) => rows,
    autoHighlight: true,
    // As SearchSelect's: a required answer changes only on a pick.
    disableClearable: Boolean(required),
    disabled,
  });
  const showing = combo.groupedOptions;

  let note: ReactNode = null;
  if (typed.length >= minChars && (typed !== settled || searching))
    note = <LoadingNote label={searchingLabel} />;
  else if (failed)
    note = (
      <span role="alert" className="flex flex-wrap items-center gap-x-2">
        The search didn’t go through.
        <button
          type="button"
          // Keeps the box focused, so the popup stays open for the answer.
          onMouseDown={(e) => e.preventDefault()}
          onClick={() => setAttempt((n) => n + 1)}
          className="text-brk-accent hover:underline"
        >
          Try again
        </button>
      </span>
    );
  else if (result && showing.length === 0)
    note = result.emptyMessage ?? "No results.";

  return (
    <ComboboxField
      combo={combo}
      label={label}
      error={error}
      detail={detail}
      required={required}
      optional={optional}
      placeholder={placeholder}
      clearable={!required && !disabled && selected !== null}
      note={note}
      options={showing.map((item, index) => {
        const { key, ...props } = combo.getOptionProps({ option: item, index });
        return (
          <li key={key} {...props} className={FIELD_OPTION}>
            {renderOption(item)}
          </li>
        );
      })}
    />
  );
}
